/* eslint-disable @typescript-eslint/indent */
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useParams } from 'react-router-dom';
import { FileDropZone, Loader, SdiTrash, styles } from '@sdir/sds';
import { Field, FieldArray, Form, Formik, FormikErrors } from 'formik';
import styled from 'styled-components';
import { AttachmentResult, AttachmentType } from '@sdir/httpclient/lib/clients/aps/application';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { NewAttachment, SelectOption } from '../../../types';
import { FormSelect, SubmitSequenceSummary } from '../../Molecules';
import { AttachmentTypeTranslationMap } from '../../../constants/translationMaps';
import { useCurrentApplication, useSubmitSequence } from '../../../services/hooks';

import { routes } from '../../../constants/routes';
import { FileButton } from '../../Atoms/FileButton';
import {
  buildSubmitAttachmentUpdatesSequence,
  SubmitAttachmentUpdatesParams,
  SubmitAttachmentUpdatesTracker,
} from '../../../helpers/attachmentApiHelpers';
import { ApplicationLayout } from '../../Organisms';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

interface FileInput {
  file?: File | null;
  type: AttachmentType | null;
  attachment?: AttachmentResult | null;
}

interface AddFilesInput {
  files: FileInput[];
}

export const AddFilesPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const pageParams = useParams();
  const [submitTracker, setSubmitTracker] = useState<SubmitAttachmentUpdatesTracker>({});

  const { application, refresh } = useCurrentApplication();

  const [addFilesError, setAddFilesError] = useState<Error | string | null>(null);

  const { isSubmitting, results, submit } = useSubmitSequence<
    SubmitAttachmentUpdatesParams,
    SubmitAttachmentUpdatesTracker
  >(buildSubmitAttachmentUpdatesSequence);

  const handleExitClick = useCallback(() => {
    navigate(generatePath(routes.application.uploadMissingDocumentation, pageParams));
  }, [navigate, pageParams]);

  const handleSubmitCallback = useCallback(
    async (values: AddFilesInput) => {
      setAddFilesError(null);

      if (!application.result) return;

      try {
        // Course and seagoing files are submitted from their respective AddSeagoing/Course-page
        const newAttachments = values.files.filter(
          (x): x is NewAttachment => !x.attachment && !!x.file && !!x.type
        );
        const existingOtherAttachments =
          application.result.attachments?.filter((a) => !a.simulatorResultItemUri) || [];
        const removedAttachments =
          existingOtherAttachments.filter(
            (a) => !values.files.find((x) => x.attachment?.uid === a.uid)
          ) || [];
        const updatedAttachments =
          values.files.filter(
            (x): x is { file?: File | null; type: AttachmentType; attachment: AttachmentResult } =>
              !!x.attachment && (!!x.file || x.type !== x.attachment.type)
          ) || [];

        const { tracker } = await submit(
          {
            application: application.result,
            newAttachments,
            removedAttachments: removedAttachments.map((a) => a.uid || ''),
            updatedAttachments: updatedAttachments.map((x) => ({
              attachmentUid: x.attachment.uid || '',
              newFile: x.file || null,
              newType: x.type,
            })),
            attachmentGroup: null,
          },
          submitTracker
        );

        setSubmitTracker(tracker);

        if (tracker.errors) {
          setAddFilesError(tracker.errors.join(' '));
          return;
        }

        // refresh attachment list and exit
        refresh?.();
        handleExitClick();
      } catch (e: any) {
        setAddFilesError(e);
      }
    },
    [application.result, submit, submitTracker, refresh, handleExitClick]
  );

  const handleValidation = useCallback(
    (values: AddFilesInput) => {
      const errors: FormikErrors<AddFilesInput> = {};
      const requiredText = intl.formatMessage({ id: 'general.form.error' });

      if (values.files.find((f) => !f.type))
        errors.files = values.files.map<FormikErrors<FileInput>>((f) =>
          f.type ? {} : { type: requiredText }
        );

      return Object.keys(errors).length ? errors : undefined;
    },
    [intl]
  );

  const initialValues = useMemo<AddFilesInput>(() => {
    return {
      files:
        application.result?.attachments
          ?.filter((a) => !a.simulatorResultItemUri)
          .map<FileInput>((a) => ({ attachment: a, type: a.type || null })) || [],
    };
  }, [application.result]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmitCallback}
      validate={handleValidation}
      enableReinitialize
    >
      {({ values, handleSubmit }) => (
        <Form>
          <ApplicationLayout
            topHeading={intl.formatMessage({ id: 'app.requirements.table.title' })}
            heading={intl.formatMessage({ id: 'app.requirements.table.header' })}
            exitLabel={intl.formatMessage({ id: 'app.addfiles.back' })}
            onExitClick={handleExitClick}
            mainContentMaxWidth="92rem"
            canNavBack
            canNavNext
            navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
            navNextLabel={intl.formatMessage({ id: 'general.button.save' })}
            onNavNextClick={handleSubmit}
          >
            <FieldArray name="files">
              {({ push, remove }) => (
                <>
                  <OtherFilesDropZone name="files" onAdd={push} />
                  {values.files.map((x, i) => (
                    <OtherFileEditor
                      key={i}
                      name={`files.${i}`}
                      index={i}
                      value={x}
                      onRemove={remove}
                    />
                  ))}
                </>
              )}
            </FieldArray>
            {isSubmitting && <Loader />}
            <SubmitSequenceSummary results={results} />
            {addFilesError && <ErrorMessage error={addFilesError} />}
          </ApplicationLayout>
        </Form>
      )}
    </Formik>
  );
};

const OtherFilesDropZone: React.FC<{ name: string; onAdd: (addedFile: FileInput) => void }> = ({
  onAdd,
}) => {
  const intl = useIntl();

  const handleFilesAdded = useCallback(
    (files: File[]) => {
      files.forEach((f) => onAdd({ file: f, type: null }));
    },
    [onAdd]
  );

  return (
    <FileDropZone
      label={intl.formatMessage({ id: 'app.manual.drop.file' })}
      allowedTypes=".png,.jpg,.jpeg,.pdf"
      onFilesSelected={handleFilesAdded}
      onFilesDrop={handleFilesAdded}
    />
  );
};

const availableTypeOptions = [
  AttachmentType.Application,
  AttachmentType.SeagoingTime,
  AttachmentType.Course,
  AttachmentType.Education,
  AttachmentType.Photo,
  AttachmentType.PhotoCropped,
  AttachmentType.Signature,
  AttachmentType.SignatureCropped,
  AttachmentType.Na,
];

const OtherFileEditor: React.FC<{
  name: string;
  value: FileInput;
  index: number;
  onRemove: (i: number) => void;
}> = ({ name, value, index, onRemove }) => {
  const intl = useIntl();

  const documentTypeOptions: SelectOption[] = useMemo(
    () =>
      availableTypeOptions.map((o) => ({
        name: intl.formatMessage({ id: AttachmentTypeTranslationMap[o] }),
        value: o,
      })),
    [intl]
  );

  const handleRemoveClick = useCallback(() => onRemove(index), [index, onRemove]);

  const fileOrAttachment = value.file || value.attachment;

  return (
    <OtherFileContainer>
      {fileOrAttachment && (
        <FileButton
          file={fileOrAttachment}
          showSize
          onClick={handleRemoveClick}
          hoverIcon={<SdiTrash size="s" color={styles.colors.primary} />}
        />
      )}
      <Field
        name={`${name}.type`}
        component={FormSelect}
        options={documentTypeOptions}
        defaultVal={intl.formatMessage({
          id: 'app.addfiles.input.type.label',
        })}
      />
    </OtherFileContainer>
  );
};

const OtherFileContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 4rem;
  margin-bottom: 3rem;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    grid-template-columns: 1fr;
    grid-row-gap: 3rem;
  }
  @media screen and (max-width: ${({ theme }) => theme.screen.small}) {
    grid-template-columns: 1fr;
    grid-row-gap: 3rem;
  }
`;

export default AddFilesPage;
